import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { HeaderSort, EmptyTable, TablePagination } from "components/third-party/ReactTable";
import moment from "moment";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTable, Column, useGlobalFilter, useFilters, useSortBy, useExpanded, usePagination, useRowSelect, HeaderGroup, Row, Cell } from "react-table";
import { renderFilterTypes, GlobalFilter } from "utils/react-table";
import { EyeTwoTone } from "@ant-design/icons";
import React from "react";
import { Composition, FhirEntry, QuestionnaireResponse } from "../PatientFilePage";
import MainCard from "components/MainCard";
import i18n from "app/i18n";

export default function PatientHealthData({ compositionData, isLoading, isFetching }: { compositionData: FhirEntry[], isLoading: boolean, isFetching: boolean }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState<string | null>(null);

  const handleOpenDialog = (rawData: any) => {
    let parsed = rawData;

    if (typeof rawData === 'string') {
      try {
        // Corregimos comillas simples por dobles si es necesario
        const fixed = rawData.replace(/'/g, '"');
        parsed = JSON.parse(fixed);
      } catch (e) {
        // No es JSON, lo dejamos como string
      }
    }

    setDialogContent(parsed);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogContent(null);
    setDialogOpen(false);
  };

  const renderObjectAsTable = (obj: any): JSX.Element => {
    if (!obj || typeof obj !== 'object') return <></>;

    return (
      <Table size="small">
        <TableBody>
          {Object.entries(obj).map(([key, value]) => {
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
              return (
                <React.Fragment key={key}>
                  <TableRow>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      {key}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2} sx={{ pl: 2 }}>
                      {renderObjectAsTable(value)}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            } else {
              return (
                <TableRow key={key}>
                  <TableCell sx={{ fontWeight: 500 }}>{key}</TableCell>
                  <TableCell>{Array.isArray(value) ? value.join(', ') : value?.toString()}</TableCell>
                </TableRow>
              );
            }
          })}
        </TableBody>
      </Table>
    );
  };

  const filterTypes = useMemo(() => renderFilterTypes, []);

  function isComposition(resource: any): resource is Composition {
    return resource?.resourceType === 'Composition';
  }

  function isQuestionnaireResponse(resource: any): resource is QuestionnaireResponse {
    return resource?.resourceType === 'QuestionnaireResponse';
  }

  const columns = useMemo(
    () => [
      {
        Header: t('code', 'Code'),
        accessor: (row: FhirEntry) => {
          const res = row.resource;
          if (isComposition(res)) {
            return res.section?.[0]?.code?.coding?.[0]?.system ?? '-';
          }
          if (isQuestionnaireResponse(res)) {
            return res._questionnaire?.extension?.[0]?.valueString ?? '-';
          }
          return '-';
        },
        Cell: ({ value }: { value: string }) => value ?? '-'
      },
      {
        id: 'date',
        Header: t('date', 'Date'),
        accessor: (row: FhirEntry) => {
          const res = row.resource;
          if (isComposition(res)) {
            return res.date ?? '-';
          }
          if (isQuestionnaireResponse(res)) {
            return res.meta?.lastUpdated ?? '-';
          }
          return '-';
        },
        Cell: ({ value }: { value: string }) =>
          value ? moment(value).locale(i18n.language).format('l LTS') : '-'
      },
      {
        Header: t('actions', 'Actions'),
        className: 'cell-center',
        disableSortBy: true,
        Cell: ({ row }: { row: Row<{}> }) => {
          const res = (row.original as FhirEntry).resource;
          let display = '-';

          if (isComposition(res)) {
            display = res.section?.[0]?.code?.coding?.[0]?.display ?? '-';
          }
          if (isQuestionnaireResponse(res)) {
            try {
              const obj: Record<string, any> = {};

              res.item?.forEach((item) => {
                const answers = item.answer?.map((a) => {
                  if (a.valueString != null) return a.valueString;
                  if (a.valueInteger != null) return a.valueInteger;
                  return null;
                });

                obj[item.text] = answers ?? [];
              });

              display = JSON.stringify(obj, null, 2);
            } catch (e) {
              display = String(res.item ?? '-');
            }
          }

          return (
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
              <Tooltip title={t('view', 'View')}>
                <IconButton
                  color="primary"
                  onClick={() => handleOpenDialog(display)}
                >
                  <EyeTwoTone twoToneColor={theme.palette.primary.main} />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        }
      }
    ],
    [t]
  );

  const flatData = useMemo(() => {
    return compositionData
      .flat()
      .filter((entry) => {
        if (isComposition(entry.resource)) {
          const system = entry.resource.section?.[0]?.code?.coding?.[0]?.system;
          return system !== 'UM-LLM' && system !== 'UM-AUS';
        }
        if (isQuestionnaireResponse(entry.resource)) {
          return true;
        }
        return false;
      });
  }, [compositionData]);


  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    gotoPage,
    setPageSize,
    state: { globalFilter, pageIndex, pageSize },
    preGlobalFilteredRows,
    setGlobalFilter
  } = useTable(
    {
      columns: columns as Column[],
      data: flatData,
      autoResetPage: false,
      autoResetFilters: false,
      filterTypes,
      initialState: { pageIndex: 0, pageSize: 10, sortBy: [{ id: 'date', desc: true }] }
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  return (
    <>
      <MainCard content={false}>
        <Stack spacing={3}>
          <Stack
            direction={matchDownSM ? 'column' : 'row'}
            spacing={1}
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 3, pb: 0 }}
          >
            <GlobalFilter
              preGlobalFilteredRows={preGlobalFilteredRows}
              globalFilter={globalFilter}
              setGlobalFilter={setGlobalFilter}
              size="small"
            />
          </Stack>

          <Table {...getTableProps()}>
            <TableHead>
              {headerGroups.map((headerGroup: HeaderGroup<{}>) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: HeaderGroup) => (
                    <TableCell
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      <HeaderSort column={column} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody {...getTableBodyProps()}>
              {isLoading ? (
                <EmptyTable msg={t('loading', 'Loading...')} colSpan={4} />
              ) : isFetching ? (
                <EmptyTable msg={t('fetching', 'Fetching...')} colSpan={4} />
              ) : page.length > 0 ? (
                page.map((row: Row, i: number) => {
                  prepareRow(row);
                  return (
                    <Fragment key={i}>
                      <TableRow {...row.getRowProps()}>
                        {row.cells.map((cell: Cell) => (
                          <TableCell {...cell.getCellProps()}>
                            {cell.render('Cell')}
                          </TableCell>
                      ))}
                    </TableRow>
                  </Fragment>
                );
              })
            ) : (
              <EmptyTable msg={t('no_data_found', 'Data not found')} colSpan={4} />
            )}

            <TableRow sx={{ '&:hover': { bgcolor: 'transparent !important' } }}>
              <TableCell colSpan={4} sx={{ p: 2, py: 3 }}>
                <TablePagination
                  gotoPage={gotoPage}
                  rows={rows}
                  setPageSize={setPageSize}
                  pageSize={pageSize}
                  pageIndex={pageIndex}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Stack>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="md">
        <DialogTitle>{t('extractions', 'Extractions')}</DialogTitle>
        <DialogContent>
          {typeof dialogContent === 'object'
            ? renderObjectAsTable(dialogContent)
            : dialogContent?.toString()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}variant="outlined" color="secondary">{t('close', 'Close')}</Button>
        </DialogActions>
      </Dialog>
      </MainCard>
    </>
  );
};